import React, { useState, useCallback } from "react";
import {
  useGetDeployNotificationsQuery,
  useUpdateDeployNotificationMutation,
} from "../endpoints/deploy-notifications.endpoints";
import DeployNotificationsTable from "../components/requests/deploy-notifications-table";
import DeployRequestDrawer from "../components/requests/deploy-request-drawer";
import { DeployNotification } from "../types/deploy-notification.types";

const RequestsTab: React.FC = () => {
  const { data: notifications = [], isLoading, isError, refetch } =
    useGetDeployNotificationsQuery();
  const [updateDeployNotification] = useUpdateDeployNotificationMutation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<DeployNotification | null>(null);

  const handleRowClick = useCallback((notification: DeployNotification) => {
    setSelectedNotification(notification);
    setDrawerOpen(true);
  }, []);

  const handleDrawerOpenChange = useCallback((open: boolean) => {
    setDrawerOpen(open);
    if (!open) setSelectedNotification(null);
  }, []);

  const handleMarkComplete = useCallback(
    async(notification: DeployNotification) => {
      try {
        await updateDeployNotification({
          id: notification.id,
          is_complete: true,
        }).unwrap();
        const result = await refetch();
        const updated = result.data?.find((n) => n.id === notification.id);
        if (updated) setSelectedNotification(updated);
      } catch {
        // Error handled by mutation
      }
    },
    [updateDeployNotification, refetch]
  );

  const handleDelete = useCallback(
    async(notification: DeployNotification) => {
      try {
        await updateDeployNotification({
          id: notification.id,
          status: "archived",
        }).unwrap();
        await refetch();
        setDrawerOpen(false);
        setSelectedNotification(null);
      } catch {
        // Error handled by mutation
      }
    },
    [updateDeployNotification, refetch]
  );

  return (
    <div className="h-full overflow-y-auto">
      <p className="text-sm text-gray-500 mb-4">
        Creative run requests to deploy creatives. Click a row to view details.
      </p>
      {isLoading ? (
        <div className="py-12 text-center text-gray-500">
          Loading requests…
        </div>
      ) : isError ? (
        <div className="py-12 text-center text-red-600">
          Failed to load requests. Please try again.
        </div>
      ) : (
        <>
          <DeployNotificationsTable
            notifications={notifications}
            onRowClick={handleRowClick}
          />
          <DeployRequestDrawer
            open={drawerOpen}
            onOpenChange={handleDrawerOpenChange}
            notification={selectedNotification}
            onMarkComplete={handleMarkComplete}
            onDelete={handleDelete}
          />
        </>
      )}
    </div>
  );
};

export default RequestsTab;
